import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Divider,
  useMediaQuery,
  useTheme,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Download as DownloadIcon, ContentCopy as CopyIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { type HashatarConfig } from '@hashatar/core';
import ConfigPanel from './components/ConfigPanel';
import AnimatedAvatar from './components/AnimatedAvatar';
import CopyPasteDemo from './components/CopyPasteDemo';
import InstallationSection from './components/InstallationSection';

const EXAMPLE_INPUTS = [
  'chris.jackson@university.edu',
  'john.doe@example.com',
  '1JwSSubhmg6iPtRjtyqhUYYH7bZg3Lfy1T',
  'bc1qa5wkgaew2dkv56kfvj49j0av5nml45x9ek9hz6',
  'CY17 0020 0128 0000 0012 0052 7600',
  '+31 6 12345678',
];

// Bitcoin donation address
const BITCOIN_ADDRESS = '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa';

// API URL from environment variable, defaults to local Docker port
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3210';

// Authentication types
interface User {
  id: string;
  email: string;
  credits: number;
}

interface ApiToken {
  id: string;
  name: string | null;
  prefix: string;
  displayToken: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

function App() {
  const [input, setInput] = useState('chris.jackson@university.edu');
  const [config, setConfig] = useState<HashatarConfig>({
    size: 64,
  });
  
  // Authentication state
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  // API Tokens state
  const [apiTokens, setApiTokens] = useState<ApiToken[]>([]);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  
  // Credits and billing state
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  const [autoRechargeEnabled, setAutoRechargeEnabled] = useState(true);
  const [autoRechargeAmount, setAutoRechargeAmount] = useState(2500); // $25 in cents
  const [autoRechargeThreshold, setAutoRechargeThreshold] = useState(100000); // 100k credits
  
  // Log API URL for debugging
  console.log('API URL:', API_URL);
  
  // Responsive breakpoint detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Progressive animation state for the title
  const [animatedText, setAnimatedText] = useState('H');
  const targetText = 'Hashatar';
  const backgroundRef = useRef<HTMLDivElement>(null);
  const bitcoinRef = useRef<HTMLDivElement>(null);
  const demoRef = useRef<HTMLDivElement>(null);
  const demoPercentage = useMemo(() => {
    if (config.size > 64) {
      return '100%';
    }

    return `${(config.size / 64) * 100}%`;
  }, [config.size]);

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Handle payment result from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    
    if (paymentStatus === 'success') {
      alert('Payment successful! Your credits have been added to your account.');
      // Reload user data to show updated credits
      checkAuthStatus();
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      alert('Payment was cancelled. No charges were made.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Set up the progressive animation
  useEffect(() => {
    const duration = 30000; // 30 seconds
    const steps = targetText.length;
    const stepDuration = duration / steps;

    const interval = setInterval(() => {
      setAnimatedText(current => {
        if (current.length < targetText.length) {
          return targetText.slice(0, current.length + 1);
        }
        // Reset animation after completion
        setTimeout(() => setAnimatedText('H'), 2000);
        return current;
      });
    }, stepDuration);

    return () => clearInterval(interval);
  }, []);

  const handleInputChange = useCallback((newInput: string) => {
    setInput(newInput);
  }, []);

  const handleConfigChange = useCallback((newConfig: HashatarConfig) => {
    setConfig(newConfig);
  }, []);

  const downloadAvatar = () => {
    const canvas = document.querySelector('#demo-avatar canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `hashatar-${input.replace(/[^a-zA-Z0-9]/g, '_')}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  const copyBitcoinAddress = () => {
    navigator.clipboard.writeText(BITCOIN_ADDRESS);
  };

  // Authentication API functions
  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/auth/me`, {
        method: 'GET',
        credentials: 'include', // Include cookies
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        // Load API tokens and auto-recharge settings if user is authenticated
        loadApiTokens();
        loadAutoRechargeSettings();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');

    try {
      const response = await fetch(`${API_URL}/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies
        body: JSON.stringify({
          email: loginEmail,
          password: loginPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data.user);
        setLoginEmail('');
        setLoginPassword('');
        setLoginError('');
        // Load API tokens and auto-recharge settings after successful login
        loadApiTokens();
        loadAutoRechargeSettings();
      } else {
        setLoginError(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');

    try {
      const response = await fetch(`${API_URL}/v1/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies
        body: JSON.stringify({
          email: loginEmail,
          password: loginPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data.user);
        setLoginEmail('');
        setLoginPassword('');
        setLoginError('');
        // Load API tokens and auto-recharge settings after successful signup
        loadApiTokens();
        loadAutoRechargeSettings();
      } else {
        setLoginError(data.message || 'Signup failed');
      }
    } catch (error) {
      console.error('Signup error:', error);
      setLoginError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    setIsLoading(true);
    
    try {
      await fetch(`${API_URL}/v1/auth/logout`, {
        method: 'POST',
        credentials: 'include', // Include cookies
      });
      
      setUser(null);
      setApiTokens([]); // Clear tokens on logout
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear user state even if API call fails
      setUser(null);
      setApiTokens([]);
    } finally {
      setIsLoading(false);
    }
  };

  // API Token management functions
  const loadApiTokens = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/auth/tokens`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setApiTokens(data.tokens || []);
      }
    } catch (error) {
      console.error('Failed to load API tokens:', error);
    }
  };

  const createApiToken = async () => {
    setIsLoadingTokens(true);
    try {
      const response = await fetch(`${API_URL}/v1/auth/tokens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: newTokenName || undefined,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setCreatedToken(data.token.token); // Store the full token for display
        setNewTokenName('');
        loadApiTokens(); // Refresh the list
      } else {
        console.error('Failed to create token:', data.message);
      }
    } catch (error) {
      console.error('Token creation error:', error);
    } finally {
      setIsLoadingTokens(false);
    }
  };

  const revokeApiToken = async (tokenId: string) => {
    setIsLoadingTokens(true);
    try {
      const response = await fetch(`${API_URL}/v1/auth/tokens/${tokenId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        loadApiTokens(); // Refresh the list
      } else {
        console.error('Failed to revoke token');
      }
    } catch (error) {
      console.error('Token revocation error:', error);
    } finally {
      setIsLoadingTokens(false);
    }
  };

  // Credits and payment functions
  const purchaseCredits = async (amount: number) => {
    setIsLoadingPayment(true);
    try {
      // Get current URL for success/cancel redirects
      const currentUrl = window.location.origin + window.location.pathname;
      const successUrl = `${currentUrl}?payment=success`;
      const cancelUrl = `${currentUrl}?payment=cancelled`;

      const response = await fetch(`${API_URL}/v1/payment/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          amount, 
          successUrl, 
          cancelUrl 
        }),
      });

      const data = await response.json();

      if (response.ok && data.url) {
        // Redirect to Stripe Checkout
        console.log('Redirecting to Stripe Checkout:', data);
        window.location.href = data.url;
      } else {
        console.error('Checkout session creation failed:', data.message);
        alert(`Payment failed: ${data.message}`);
        setIsLoadingPayment(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment failed: Network error');
      setIsLoadingPayment(false);
    }
    // Note: Don't set loading to false here if redirecting successfully
    // The page will navigate away to Stripe
  };

  const loadAutoRechargeSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/auto-recharge/status`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAutoRechargeEnabled(data.enabled || false);
        if (data.settings) {
          setAutoRechargeAmount(data.settings.amount || 2500);
          setAutoRechargeThreshold(data.settings.threshold || 100000);
        }
      }
    } catch (error) {
      console.error('Failed to load auto-recharge settings:', error);
    }
  };

  const toggleAutoRecharge = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/auto-recharge/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          enabled: !autoRechargeEnabled,
          amount: autoRechargeAmount,
          threshold: autoRechargeThreshold,
        }),
      });

      if (response.ok) {
        setAutoRechargeEnabled(!autoRechargeEnabled);
      } else {
        console.error('Failed to update auto-recharge settings');
      }
    } catch (error) {
      console.error('Auto-recharge toggle error:', error);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* SECTION 1: Landing Page */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Bokeh background that updates with the animated text */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: -1 }} ref={backgroundRef}>
          <AnimatedAvatar text={animatedText} size={128} containerRef={backgroundRef} />
        </Box>

        {/* Colorful animated title with progressive highlighting */}
        <Typography
          variant="h1"
          component="h1"
          sx={{
            fontSize: { xs: '4rem', md: '6rem', lg: '8rem' },
            fontWeight: 'bold',
            textAlign: 'center',
            mb: 4,
            textShadow: '0 0 30px rgba(255, 255, 255, 0.3)',
          }}
        >
          {/* Animated portion - the part that's been revealed */}
          <span
            style={{
              background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4, #ffeaa7, #dda0dd)',
              backgroundSize: '400% 400%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              animation: 'gradientShift 3s ease-in-out infinite alternate',
              transition: 'all 0.5s ease-in-out',
            }}
          >
            {animatedText}
          </span>
          {/* Remaining muted portion */}
          <span
            style={{
              background: 'linear-gradient(45deg, #666, #888)',
              backgroundSize: '400% 400%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              transition: 'all 0.5s ease-in-out',
            }}
          >
            {targetText.slice(animatedText.length)}
          </span>
          <style>
            {`
              @keyframes gradientShift {
                0% { background-position: 0% 50%; }
                100% { background-position: 100% 50%; }
              }
            `}
          </style>
        </Typography>

        {/* Animated avatar below title - updates with progressive text */}
        <Box sx={{ display: 'flex', justifyContent: 'center', borderRadius: 4, overflow: 'hidden' }}>
          <AnimatedAvatar text={animatedText} />
        </Box>
      </Box>

      {/* SECTION 2: Copy-Paste Demo */}
      <CopyPasteDemo />

      {/* SECTION 3: Try it out */}
      <Box
        sx={{
          backgroundColor: '#fafafa',
          py: { xs: 4, md: 8 },
          px: { xs: 2, sm: 3, md: 4 },
          minHeight: '100vh',
          overflow: 'hidden', // Prevent horizontal scrolling
        }}
      >
        <Container 
          maxWidth="md" 
          sx={{ 
            px: { xs: 1, sm: 2, md: 3 },
            maxWidth: '100%', // Ensure container never exceeds viewport width
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              textAlign: 'center',
              mb: 4,
              fontWeight: 'bold',
              color: '#333',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
            }}
          >
            Hashatar: the visual check for important data
          </Typography>

          <Grid container spacing={{ xs: 1, sm: 2 }} justifyContent="center" flexDirection={{ xs: 'column-reverse', md: 'row' }}>
            {/* Left Panel - Examples and Size Controls */}
            <Grid item xs={12} md={6}>
              <Stack spacing={2.5} sx={{ alignItems: 'center' }}>
                {/* Examples */}
                <Paper 
                  sx={{ 
                    p: 2.5, 
                    height: 'fit-content',
                    border: '1px solid #e0e0e0',
                    boxShadow: 'none',
                    width: '100%',
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Examples
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Click any example to try it:
                  </Typography>
                  <Stack spacing={1}>
                    {EXAMPLE_INPUTS.map(example => (
                      <Button
                        key={example}
                        onClick={() => handleInputChange(example)}
                        variant={input === example ? 'contained' : 'outlined'}
                        sx={{ 
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          borderRadius: '4px',
                          padding: '12px 16px',
                          fontSize: '0.875rem',
                          fontWeight: 'normal',
                          textTransform: 'none',
                          whiteSpace: 'normal',
                          wordBreak: 'break-all',
                          minHeight: '48px',
                          backgroundColor: 'transparent',
                          color: input === example ? '#2e7d32' : '#333',
                          border: '1px solid #4caf50',
                          borderColor: input === example ? '#2e7d32' : '#ddd',
                          boxSizing: 'border-box',
                          boxShadow: input === example ? '0 0 10px rgba(76, 175, 80, 0.3)' : 'none',
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                            borderColor: '#4caf50',
                            boxShadow: '0 0 8px rgba(76, 175, 80, 0.2)',
                          }
                        }}
                      >
                        {example}
                      </Button>
                    ))}
                  </Stack>
                </Paper>

                {/* Size Controls */}
                <Paper 
                  sx={{ 
                    p: 2.5, 
                    height: 'fit-content',
                    border: '1px solid #e0e0e0',
                    boxShadow: 'none',
                    width: '100%',
                  }}
                >
                  <ConfigPanel config={config} onConfigChange={handleConfigChange} />
                </Paper>
              </Stack>
            </Grid>

            {/* Right Panel - Input and Avatar */}
            <Grid item xs={12} md={6}>
              <Stack sx={{ alignItems: 'center' }}>
                {/* Text Input Above Avatar */}
                <Paper 
                  sx={{ 
                    p: 2.5, 
                    mb: 2.5, 
                    width: 400,
                    border: '1px solid #e0e0e0',
                    boxShadow: 'none',
                  }}
                >
                  <TextField
                    fullWidth
                    label="Enter any text"
                    value={input}
                    onChange={e => handleInputChange(e.target.value)}
                    placeholder="Type anything..."
                    variant="outlined"
                    helperText="The avatar will update automatically as you type"
                  />
                </Paper>

                {/* Centered Avatar */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 400,
                    height: 400,
                  }}
                >
                  <Box 
                    sx={{ 
                      height: demoPercentage, 
                      width: demoPercentage,
                      borderRadius: config.size >= 32 ? '8px' : '4px',
                      overflow: 'hidden',
                    }} 
                    ref={demoRef} 
                    id="demo-avatar"
                  >
                    <AnimatedAvatar text={input} size={config.size} containerRef={demoRef} />
                  </Box>
                </Box>

                {/* Avatar Details */}
                <Box sx={{ mt: 3, textAlign: 'center', width: 400 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Input:</strong> {input}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Size:</strong> {config.size}x{config.size} pixels
                  </Typography>
                </Box>

                {/* Download Button */}
                <Box sx={{ mt: 3.5, textAlign: 'center', width: 400 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<DownloadIcon />}
                    onClick={downloadAvatar}
                    sx={{ 
                      mb: 2,
                      borderRadius: '4px',
                      backgroundColor: 'transparent',
                      color: '#2e7d32',
                      border: '1px solid #4caf50',
                      borderColor: '#4caf50',
                      boxSizing: 'border-box',
                      boxShadow: '0 0 10px rgba(76, 175, 80, 0.3)',
                      fontWeight: 'normal',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        borderColor: '#2e7d32',
                        boxShadow: '0 0 12px rgba(76, 175, 80, 0.4)',
                      }
                    }}
                  >
                    Download PNG
                  </Button>
                  <Typography variant="body2" color="text.secondary" display="block">
                    This avatar is deterministic - the same input will always generate the same pattern
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* SECTION 4: Authentication - HIDDEN */}
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          py: 8,
          minHeight: '100vh',
          display: 'none',
        }}
      >
        <Container maxWidth="xl">
          <Paper
            sx={{
              p: 6,
              border: '2px solid #ccc',
              borderRadius: 2,
              boxShadow: 'none',
              position: 'relative',
              minHeight: '80vh',
            }}
          >
            {/* Responsive Layout */}
            {isMobile ? (
              // Mobile Layout: Login at top, API tokens below
              <Box>
                {/* Login/Signup Form - Top on Mobile */}
                <Box sx={{ mb: 4, p: 3, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                  {isCheckingAuth ? (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Checking authentication...
                      </Typography>
                    </Box>
                  ) : user ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Welcome, {user.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                        Credits: {user.credits.toLocaleString()}
                      </Typography>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={handleLogout}
                        disabled={isLoading}
                        sx={{ fontSize: '0.75rem', py: 0.5 }}
                      >
                        {isLoading ? 'Logging out...' : 'Logout'}
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block', textAlign: 'center' }}>
                        Sign in for larger avatars & API access
                      </Typography>
                      
                      <form onSubmit={handleLogin}>
                        <Stack spacing={2}>
                          <TextField
                            size="small"
                            label="Email"
                            type="email"
                            variant="outlined"
                            fullWidth
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            disabled={isLoading}
                          />
                          <TextField
                            size="small"
                            label="Password"
                            type="password"
                            variant="outlined"
                            fullWidth
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            disabled={isLoading}
                          />
                          
                          {loginError && (
                            <Alert severity="error" sx={{ fontSize: '0.875rem' }}>
                              {loginError}
                            </Alert>
                          )}
                          
                          <Stack direction="row" spacing={2}>
                            <Button 
                              type="submit"
                              variant="contained" 
                              size="medium" 
                              disabled={isLoading || !loginEmail || !loginPassword}
                              fullWidth
                            >
                              {isLoading ? 'Logging in...' : 'Log In'}
                            </Button>
                            <Button 
                              variant="outlined" 
                              size="medium" 
                              onClick={handleSignup}
                              disabled={isLoading || !loginEmail || !loginPassword}
                              fullWidth
                            >
                              Sign Up
                            </Button>
                          </Stack>
                        </Stack>
                      </form>
                    </Box>
                  )}
                </Box>

                {/* Credits & Billing Section - Mobile */}
                {user && (
                  <Box sx={{ mb: 4 }}>
                    <Paper sx={{ p: 3, border: '1px solid #e0e0e0', mb: 3 }}>
                      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                        💰 Credits & Billing
                      </Typography>
                      
                      {/* Current Balance */}
                      <Box sx={{ mb: 3, textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                          {user.credits.toLocaleString()} credits
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Current balance
                        </Typography>
                      </Box>
                      
                      {/* Purchase Buttons */}
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Purchase Credits
                      </Typography>
                      <Stack spacing={2} sx={{ mb: 3 }}>
                        <Button
                          variant="outlined"
                          onClick={() => purchaseCredits(500)}
                          disabled={isLoadingPayment}
                          fullWidth
                        >
                          💳 Buy $5 - 1M credits
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => purchaseCredits(2500)}
                          disabled={isLoadingPayment}
                          fullWidth
                        >
                          💳 Buy $25 - 5.5M credits (+10% bonus)
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => purchaseCredits(10000)}
                          disabled={isLoadingPayment}
                          fullWidth
                        >
                          💳 Buy $100 - 25M credits (+25% bonus)
                        </Button>
                      </Stack>
                      
                      {/* Auto-Recharge */}
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Auto-Recharge
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={autoRechargeEnabled}
                            onChange={toggleAutoRecharge}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">
                              Enable Auto-Recharge
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              When balance &lt; 100,000 credits, add $25 (5.5M credits)
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  </Box>
                )}

                {/* API Tokens Section - Mobile */}
                {user && (
                  <Box>
                    <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', color: '#333', mb: 3 }}>
                      API Integration
                    </Typography>
                    
                    {/* API Tokens Section */}
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                      🔑 API Tokens
                    </Typography>
                    
                    {/* Created Token Alert */}
                    {createdToken && (
                      <Alert 
                        severity="success" 
                        sx={{ mb: 3 }}
                        action={
                          <Button 
                            size="small" 
                            onClick={() => {
                              navigator.clipboard.writeText(createdToken);
                              setCreatedToken(null);
                            }}
                            startIcon={<CopyIcon />}
                          >
                            Copy & Close
                          </Button>
                        }
                      >
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Token created!</strong> Save this securely - it won't be shown again.
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', fontSize: '0.8rem' }}>
                          {createdToken}
                        </Typography>
                      </Alert>
                    )}
                    
                    {/* Token List */}
                    <Paper sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
                      {apiTokens.length > 0 ? (
                        <List>
                          {apiTokens.map((token, index) => (
                            <Box key={token.id}>
                              <ListItem>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                      {token.name || 'Unnamed Token'}
                                    </Typography>
                                  }
                                    secondary={
                                      <Box>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                          {token.displayToken || `${token.prefix}****...****`}
                                        </Typography>
                                        <br />
                                        <Typography variant="caption" color="text.secondary">
                                          Created: {new Date(token.createdAt).toLocaleDateString()}
                                        </Typography>
                                      </Box>
                                    }
                                />
                                <ListItemSecondaryAction>
                                  <IconButton
                                    edge="end"
                                    onClick={() => revokeApiToken(token.id)}
                                    disabled={isLoadingTokens}
                                    size="small"
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                              {index < apiTokens.length - 1 && <Divider />}
                            </Box>
                          ))}
                        </List>
                      ) : (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            No API tokens created yet
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                    
                    {/* Create Token Section */}
                    <Box sx={{ mb: 4 }}>
                      <Stack spacing={2}>
                        <TextField
                          size="small"
                          label="Token name (optional)"
                          value={newTokenName}
                          onChange={(e) => setNewTokenName(e.target.value)}
                          disabled={isLoadingTokens}
                          fullWidth
                        />
                        <Button
                          variant="contained"
                          onClick={createApiToken}
                          disabled={isLoadingTokens}
                          startIcon={isLoadingTokens ? null : <span>➕</span>}
                          fullWidth
                        >
                          {isLoadingTokens ? 'Creating...' : 'Create Token'}
                        </Button>
                      </Stack>
                    </Box>
                    
                    {/* Usage Guide */}
                    <Box sx={{ 
                      backgroundColor: '#f8f9fa', 
                      p: 3, 
                      borderRadius: 1, 
                      border: '1px solid #e9ecef'
                    }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        📋 API Usage Guide
                      </Typography>
                      
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Use your API tokens to generate avatars programmatically:
                      </Typography>
                      
                      <Box sx={{ 
                        backgroundColor: '#1a1a1a', 
                        p: 2, 
                        borderRadius: 1, 
                        mb: 2,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: '#00ff41',
                        overflowX: 'auto'
                      }}>
                        <Typography component="pre" sx={{ color: '#00ff41', fontSize: 'inherit' }}>
{`curl -H "Authorization: Bearer hsh_token" \\
     "${API_URL}/v1/h/hello?size=32"`}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        • Free: sizes ≤ 16x16<br />
                        • Paid: sizes &gt; 16x16 (requires credits)<br />
                        • Formats: PNG, SVG, HTML, JSON
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              // Desktop Layout: Login top-right, API tokens on left
              <Box sx={{ position: 'relative' }}>
                {/* Login/Signup Form - Top Right Corner on Desktop */}
                <Box sx={{ position: 'absolute', top: 0, right: 0, width: 280, zIndex: 1 }}>
                  {isCheckingAuth ? (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Checking authentication...
                      </Typography>
                    </Box>
                  ) : user ? (
                    <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Welcome, {user.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                        Credits: {user.credits.toLocaleString()}
                      </Typography>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={handleLogout}
                        disabled={isLoading}
                        sx={{ fontSize: '0.75rem', py: 0.5 }}
                      >
                        {isLoading ? 'Logging out...' : 'Logout'}
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Sign in for larger avatars & API access
                      </Typography>
                      
                      <form onSubmit={handleLogin}>
                        <Stack spacing={1}>
                          <TextField
                            size="small"
                            label="Email"
                            type="email"
                            variant="outlined"
                            fullWidth
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            disabled={isLoading}
                            sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                          />
                          <TextField
                            size="small"
                            label="Password"
                            type="password"
                            variant="outlined"
                            fullWidth
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            disabled={isLoading}
                            sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                          />
                          
                          {loginError && (
                            <Typography variant="caption" color="error" sx={{ fontSize: '0.7rem' }}>
                              {loginError}
                            </Typography>
                          )}
                          
                          <Stack direction="row" spacing={1}>
                            <Button 
                              type="submit"
                              variant="contained" 
                              size="small" 
                              disabled={isLoading || !loginEmail || !loginPassword}
                              sx={{ fontSize: '0.75rem', py: 0.5 }}
                            >
                              {isLoading ? 'Logging in...' : 'Log In'}
                            </Button>
                            <Button 
                              variant="outlined" 
                              size="small" 
                              onClick={handleSignup}
                              disabled={isLoading || !loginEmail || !loginPassword}
                              sx={{ fontSize: '0.75rem', py: 0.5 }}
                            >
                              Sign Up
                            </Button>
                          </Stack>
                        </Stack>
                      </form>
                    </Box>
                  )}
                </Box>
                
                {/* Main Content Area - API Tokens */}
                <Box sx={{ pr: isMobile ? 0 : 32 }}>
                  <Typography variant="h3" component="h2" sx={{ fontWeight: 'bold', color: '#333', mb: 4 }}>
                    API Integration
                  </Typography>
                  
                  {user ? (
                    <Box>
                      {/* Credits & Billing Section - Desktop */}
                      <Paper sx={{ p: 3, border: '1px solid #e0e0e0', mb: 4 }}>
                        <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                          💰 Credits & Billing
                        </Typography>
                        
                        <Grid container spacing={3}>
                          {/* Current Balance */}
                          <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                {user.credits.toLocaleString()}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                credits available
                              </Typography>
                            </Box>
                          </Grid>
                          
                          {/* Purchase Buttons */}
                          <Grid item xs={12} md={5}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                              Purchase Credits
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => purchaseCredits(500)}
                                disabled={isLoadingPayment}
                              >
                                💳 $5 (1M)
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => purchaseCredits(2500)}
                                disabled={isLoadingPayment}
                              >
                                💳 $25 (5.5M)
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => purchaseCredits(10000)}
                                disabled={isLoadingPayment}
                              >
                                💳 $100 (25M)
                              </Button>
                            </Stack>
                          </Grid>
                          
                          {/* Auto-Recharge */}
                          <Grid item xs={12} md={3}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                              Auto-Recharge
                            </Typography>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={autoRechargeEnabled}
                                  onChange={toggleAutoRecharge}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">
                                    Enable Auto-Recharge
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    $25 when &lt; 100k credits
                                  </Typography>
                                </Box>
                              }
                            />
                          </Grid>
                        </Grid>
                      </Paper>

                      {/* API Tokens Section */}
                      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                        🔑 API Tokens
                      </Typography>
                      
                      {/* Created Token Alert */}
                      {createdToken && (
                        <Alert 
                          severity="success" 
                          sx={{ mb: 3 }}
                          action={
                            <Button 
                              size="small" 
                              onClick={() => {
                                navigator.clipboard.writeText(createdToken);
                                setCreatedToken(null);
                              }}
                              startIcon={<CopyIcon />}
                            >
                              Copy & Close
                            </Button>
                          }
                        >
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Token created successfully!</strong> Save this token securely - it won't be shown again.
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {createdToken}
                          </Typography>
                        </Alert>
                      )}
                      
                      {/* Token List */}
                      <Paper sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
                        {apiTokens.length > 0 ? (
                          <List>
                            {apiTokens.map((token, index) => (
                              <Box key={token.id}>
                                <ListItem>
                                  <ListItemText
                                    primary={
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {token.name || 'Unnamed Token'}
                                      </Typography>
                                    }
                                    secondary={
                                      <Box>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                          {token.displayToken || `${token.prefix}****...****`}
                                        </Typography>
                                        <br />
                                        <Typography variant="caption" color="text.secondary">
                                          Created: {new Date(token.createdAt).toLocaleDateString()}
                                          {token.lastUsedAt && ` • Last used: ${new Date(token.lastUsedAt).toLocaleDateString()}`}
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                  <ListItemSecondaryAction>
                                    <IconButton
                                      edge="end"
                                      onClick={() => revokeApiToken(token.id)}
                                      disabled={isLoadingTokens}
                                      size="small"
                                      color="error"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </ListItemSecondaryAction>
                                </ListItem>
                                {index < apiTokens.length - 1 && <Divider />}
                              </Box>
                            ))}
                          </List>
                        ) : (
                          <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              No API tokens created yet
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                      
                      {/* Create Token Section */}
                      <Box sx={{ mb: 4 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <TextField
                            size="small"
                            label="Token name (optional)"
                            value={newTokenName}
                            onChange={(e) => setNewTokenName(e.target.value)}
                            disabled={isLoadingTokens}
                            sx={{ minWidth: 200 }}
                          />
                          <Button
                            variant="contained"
                            onClick={createApiToken}
                            disabled={isLoadingTokens}
                            startIcon={isLoadingTokens ? null : <span>➕</span>}
                          >
                            {isLoadingTokens ? 'Creating...' : 'Create Token'}
                          </Button>
                        </Stack>
                      </Box>
                      
                      {/* Usage Guide */}
                      <Box sx={{ 
                        backgroundColor: '#f8f9fa', 
                        p: 3, 
                        borderRadius: 1, 
                        border: '1px solid #e9ecef'
                      }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          📋 API Usage Guide
                        </Typography>
                        
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          Use your API tokens to generate avatars programmatically:
                        </Typography>
                        
                        <Box sx={{ 
                          backgroundColor: '#1a1a1a', 
                          p: 2, 
                          borderRadius: 1, 
                          mb: 2,
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          color: '#00ff41',
                          overflowX: 'auto'
                        }}>
                          <Typography component="pre" sx={{ color: '#00ff41', fontSize: 'inherit' }}>
{`curl -H "Authorization: Bearer hsh_your_token_here" \\
     "${API_URL}/v1/h/hello?size=32"`}
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary">
                          • Free tier: sizes ≤ 16x16 (no authentication required)<br />
                          • Paid tier: sizes &gt; 16x16 (requires authentication + credits)<br />
                          • Available formats: PNG (/h/), SVG (/svg/), HTML (/embed/), JSON (/raw/)
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ py: 8, textAlign: 'center' }}>
                      <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
                        Sign in to access API features
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Create an account to generate API tokens, purchase credits, and access larger avatar sizes.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>

      {/* SECTION 5: Installation and Donate - HIDDEN */}
      <Box
        sx={{
          backgroundColor: '#1a1a1a',
          color: '#e0e0e0',
          py: 8,
          fontFamily: 'Monaco, "Lucida Console", monospace',
          display: 'none',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            sx={{
              textAlign: 'center',
              mb: 6,
              fontWeight: 'bold',
              color: '#00ff41',
              fontFamily: 'Monaco, "Lucida Console", monospace',
            }}
          >
            Installation
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Paper
                sx={{
                  backgroundColor: '#2a2a2a',
                  color: '#e0e0e0',
                  p: 4,
                  fontFamily: 'Monaco, "Lucida Console", monospace',
                  border: '1px solid #444',
                  boxShadow: 'none',
                }}
              >
                <Typography variant="h6" sx={{ color: '#00ff41', mb: 2 }}>
                  NPM Package
                </Typography>
                <Box sx={{ backgroundColor: '#1a1a1a', p: 2, borderRadius: 1, mb: 3 }}>
                  <Typography component="pre" sx={{ color: '#00ff41' }}>
                    {`npm install hashatar`}
                  </Typography>
                </Box>

                <Typography variant="h6" sx={{ color: '#00ff41', mb: 2 }}>
                  Usage
                </Typography>
                <Box sx={{ backgroundColor: '#1a1a1a', p: 2, borderRadius: 1, mb: 3 }}>
                  <Typography component="pre" sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
{`import { generateHashatar } from '@hashatar/core';

// Generate a 32x32 avatar
const avatar = generateHashatar('user@example.com', { 
  size: 32 
});

// avatar is a 2D array of RGB values
// [[r, g, b], [r, g, b], ...]`}
                  </Typography>
                </Box>

                <Typography variant="h6" sx={{ color: '#00ff41', mb: 2 }}>
                  CLI Tool
                </Typography>
                <Box sx={{ backgroundColor: '#1a1a1a', p: 2, borderRadius: 1 }}>
                  <Typography component="pre" sx={{ color: '#00ff41' }}>
                    {`npx hashatar "your-data-here" --size 16`}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  backgroundColor: '#2a2a2a',
                  color: '#e0e0e0',
                  p: 4,
                  fontFamily: 'Monaco, "Lucida Console", monospace',
                  border: '1px solid #444',
                  boxShadow: 'none',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h6" sx={{ color: '#00ff41', mb: 3 }}>
                  Support Development
                </Typography>

                <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
                  Bitcoin Donation Address:
                </Typography>
                
                <Box sx={{ mb: 3, width: 200, height: 200, borderRadius: 4, overflow: 'hidden' }} ref={bitcoinRef}>
                  <AnimatedAvatar text={BITCOIN_ADDRESS} size={24} containerRef={bitcoinRef} />
                </Box>
                
                <Box 
                  sx={{ 
                    backgroundColor: '#1a1a1a', 
                    p: 2, 
                    borderRadius: 2, 
                    mb: 2,
                    wordBreak: 'break-all',
                  }}
                >
                  <Typography sx={{ fontSize: '0.8rem', color: '#00ff41' }}>
                    {BITCOIN_ADDRESS}
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={copyBitcoinAddress}
                  sx={{
                    color: '#00ff41',
                    borderColor: '#00ff41',
                    '&:hover': {
                      borderColor: '#00ff41',
                      backgroundColor: 'rgba(0, 255, 65, 0.1)',
                    },
                  }}
                >
                  Copy Address
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* SECTION 6: Installation */}
      <InstallationSection />
    </Box>
  );
}

export default App;

